import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Paper, Grid, Typography, TextField, Checkbox, IconButton, Button, Stack, Snackbar, Alert } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaFileExcel } from 'react-icons/fa';
import DetailCard from '../components/DetailCard';
import LoadingOverlay from '../components/LoadingOverlay';

const path = `http://192.168.1.248:3107/api`;

export default function ProductDetail({ loggedInUser, companyData }) {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [itemCodeSearchTerm, setItemCodeSearchTerm] = useState('');
    const [DescriptionSearchTerm, setDescriptionSearchTerm] = useState('');
    const [GstNoSearchTerm, setGstNoSearchTerm] = useState('');
    const [HSNNoSearchTerm, setHSNNoSearchTerm] = useState('');
    const [GroupSearchTerm, setGroupSearchTerm] = useState('');
    const [resetFlag, setResetFlag] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const limit = 10;
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [mode, setMode] = useState('search');
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchTotalCount();
        if (mode === 'search') fetchData();
    }, [resetFlag, offset]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            let apiUrl = `${path}/product/search?mode=${mode}&offset=${offset}&limit=${limit}`;
            if (resetFlag === 1) {
                setItemCodeSearchTerm('');
                setDescriptionSearchTerm('');
                setGstNoSearchTerm('');
                setHSNNoSearchTerm('');
                setGroupSearchTerm('');
                setOffset(0);
                setResetFlag(0);
                setSelectedRows([]);
                setSelectAll(false);
            } else {
                apiUrl += `&itemCode=${itemCodeSearchTerm}&description=${DescriptionSearchTerm}&HSN=${HSNNoSearchTerm}&GST=${GstNoSearchTerm}&itemGroup=${GroupSearchTerm}`;
            }
            const res = await axios.get(apiUrl);
            if (Array.isArray(res.data)) {
                setData(offset === 0 ? res.data : [...data, ...res.data]);
            }
        } catch (e) {
            console.error('Error fetching product data', e);
            setSnackbar({ open: true, message: 'Failed to fetch data.', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTotalCount = async () => {
        try {
            const res = await axios.get(`${path}/product/count`);
            setTotalCount(res.data);
        } catch (error) {
            console.error('Error fetching total count:', error);
            setSnackbar({ open: true, message: 'Failed to fetch total count.', severity: 'error' });
        }
    };

    const funExportData = async () => {
        try {
            await setMode('export');
            const res = await axios({
                url: `${path}/product/search?mode=export`,
                method: 'GET',
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'exportedFileProduct.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading Excel file:', error);
            setSnackbar({ open: true, message: 'Download failed.', severity: 'error' });
        } finally {
            setMode('search');
        }
    };

    const funSearch = () => {
        setOffset(0);
        setMode('search');
        fetchData();
        fetchTotalCount();
    };

    const funReset = () => {
        setResetFlag(1);
    };

    const funShowMore = () => {
        if (offset + limit < totalCount) setOffset(offset + limit);
    };

    const funSelectAll = () => {
        setSelectAll(!selectAll);
        setSelectedRows(!selectAll ? data.map(item => item.Id) : []);
    };

    const funRowSelect = (id) => {
        setSelectedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const funDeleteSelectedRows = async () => {
        if (selectedRows.length === 0) return;
        try {
            console.log('Sending delete request for IDs:', selectedRows);
            const response = await axios.post(`${path}/product/delete`, {
                objAryId: selectedRows,
                userId: loggedInUser?.userId
            });
            console.log('Delete response:', response.data);

            setSnackbar({
                open: true,
                message: response.data.message || 'Deleted selected successfully.',
                severity: 'success'
            });

            funReset();
        } catch (error) {
            console.error('Delete error:', error);
            console.log('Error response:', error.response);

            let message = 'Delete failed due to an unexpected error.';

            if (error.response) {
                console.log('Error response data:', error.response.data);

                if (error.response.status === 400) {
                    // ✅ Reference exists — show clear message
                    message = 'Cannot delete: reference exists for this record.';
                } else {
                    message = `Delete failed with status ${error.response.status}: ${error.response.data?.message || 'Unknown error'}`;
                }
            } else if (error.request) {
                console.log('No response received:', error.request);
                message = 'Delete failed: No response from server.';
            } else {
                console.log('Error message:', error.message);
                message = `Delete failed, Reference Exist: ${error.message}`;
            }

            setSnackbar({ open: true, message, severity: 'error' });
        }
    };

    return (
        <>
            <LoadingOverlay isLoading={isLoading} message="Loading data..." />
            <Box p={2} display="flex" flexDirection="column" gap={2}>
            {/* Header */}
            <Paper sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => setIsVisible(v => !v)}>
                        <FilterAltIcon />
                    </IconButton>
                    <IconButton component={Link} to="/main/productMaster" color="primary">
                        <AddIcon />
                    </IconButton>
                    <Checkbox checked={selectAll} onChange={funSelectAll} />
                    <IconButton color="error" onClick={funDeleteSelectedRows} disabled={selectedRows.length === 0}>
                        <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={funExportData} color="success">
                        <FaFileExcel />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">{data.length}/{totalCount}</Typography>
            </Paper>

            {/* Filter */}
            {isVisible && (
                <Paper sx={{ p: 3, bgcolor: themeColors.primary.tint, borderRadius: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField fullWidth label="Item Code" value={itemCodeSearchTerm} onChange={e => setItemCodeSearchTerm(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField fullWidth label="Description" value={DescriptionSearchTerm} onChange={e => setDescriptionSearchTerm(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField fullWidth label="GST No." value={GstNoSearchTerm} onChange={e => setGstNoSearchTerm(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField fullWidth label="HSN No." value={HSNNoSearchTerm} onChange={e => setHSNNoSearchTerm(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField fullWidth label="Group" value={GroupSearchTerm} onChange={e => setGroupSearchTerm(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} display="flex" justifyContent="center" gap={2}>
                            <Button variant="contained" onClick={funSearch}>Search</Button>
                            <Button variant="outlined" color="error" onClick={funReset}>Cancel</Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Product Cards */}
            <Box display="flex" flexDirection="column" gap={2}>
                {data.map((item, idx) => (
                    <DetailCard
                        key={item.Id}
                        id={item.Id}
                        title={item.itemCode}
                        avatarText={item.itemCode?.[0]}
                        subtitleList={[
                            item.description,
                            `Unit: ${item.unit}`,
                            `Group: ${item.itemGroup}`,
                            `HSN: ${item.HSNNo}`,
                            `GST: ${item.TaxGST}%`,
                            `OP Bal: ${item.optionalBalance}`,
                        ]}
                        selected={selectedRows.includes(item.Id)}
                        onSelect={funRowSelect}
                        onEdit={id => navigate(`/main/editProductMaster/${id}`)}
                        backgroundColor={themeColors.neutral.surfaceAlt}
                    />
                ))}
                {/* Show More Button */}
                {data.length < totalCount && (
                    <Box textAlign="center" mt={1}>
                        <Button variant="outlined" onClick={funShowMore}>Show More</Button>
                    </Box>
                )}
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </>
    );
}